/**
 * Account — result, subscription, settings, data.
 *
 * Everything here reads from data that already exists: `user_profiles` for the
 * email, join date and payment state, the scored profile for the result row.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { formatDate } from '../i18n/detectLanguage';
import { useT } from '../i18n/useT';
import { AppShell, DeskRow } from './AppShell';
import { useAppContent } from './content';
import { useProfile } from './profile';
import { Icon } from './icons';
import { Ring } from './orb';
import { Col, Fluid, Meta, Muted, Panel, Rail, Row, useIsDesktop } from './primitives';
import { c, hair, hair2, f, scoreColor, DESKTOP } from './tokens';
import { R } from './routes';

const Group: React.FC<{ title: string; children: React.ReactNode; style?: React.CSSProperties }> = ({
    title,
    children,
    style,
}) => (
    <div style={{ padding: '20px 20px 0', ...style }}>
        <Meta style={{ fontSize: 9, letterSpacing: '0.18em', color: c.subtle, display: 'block', marginBottom: 11 }}>
            {title}
        </Meta>
        {children}
    </div>
);

const Line: React.FC<{
    label: string;
    hint?: string;
    value?: React.ReactNode;
    chev?: boolean;
    danger?: boolean;
    last?: boolean;
    to?: string;
    onClick?: () => void;
}> = ({ label, hint, value, chev, danger, last, to, onClick }) => {
    const body = (
        <Row style={{ gap: 12, padding: '14px 0', borderBottom: last ? 'none' : hair2 }}>
            <Col style={{ flex: 1, minWidth: 0 }} gap={hint ? 3 : 0}>
                <span
                    style={{
                        fontSize: 14.5,
                        color: danger ? '#f87171' : c.textPri,
                        fontWeight: 400,
                        letterSpacing: '-0.01em',
                    }}
                >
                    {label}
                </span>
                {hint && <Muted style={{ fontSize: 11.5 }}>{hint}</Muted>}
            </Col>
            {value}
            {chev && <Icon k="chev" size={12} color={c.subtle} />}
        </Row>
    );

    if (to) {
        return (
            <Link to={to} style={{ textDecoration: 'none', display: 'block' }}>
                {body}
            </Link>
        );
    }
    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    color: 'inherit',
                    cursor: 'pointer',
                }}
            >
                {body}
            </button>
        );
    }
    return body;
};

const PORTAL_URL = 'https://polar.sh/doyourstepcom/portal';

/**
 * Leaving the app for polar.sh without warning reads as a phishing redirect, so
 * the subscription line opens this explainer first: who Polar is, what the user
 * will see there, and a plain way to stay. Same tokens as the rest of the
 * surface — card, hairlines, mono meta, pill buttons.
 */
const PortalModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const t = useT();

    React.useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        // The backdrop is decoration: `role="presentation"` keeps it out of the
        // accessibility tree, and Escape (above) is the keyboard way out.
        <div
            role="presentation"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 60,
                background: 'rgba(6,6,6,0.72)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                display: 'grid',
                placeItems: 'center',
                padding: 20,
                animation: 'acctFade 0.18s ease',
            }}
        >
            <style>{`
                @keyframes acctFade { from { opacity: 0 } to { opacity: 1 } }
                @keyframes acctRise { from { opacity: 0; transform: translateY(14px) scale(0.97) } to { opacity: 1; transform: none } }
            `}</style>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t('app.account.polar.title')}
                style={{
                    width: 'min(100%, 340px)',
                    background: c.card,
                    border: hair,
                    borderRadius: 22,
                    padding: '26px 24px 24px',
                    animation: 'acctRise 0.26s cubic-bezier(0.2, 0.9, 0.3, 1)',
                }}
            >
                <div
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        border: hair,
                        background: c.pill,
                        display: 'grid',
                        placeItems: 'center',
                        marginBottom: 16,
                    }}
                >
                    <Icon k="lock" size={18} color={c.muted} />
                </div>
                <Meta style={{ fontSize: 9, letterSpacing: '0.18em', color: c.subtle, display: 'block', marginBottom: 9 }}>
                    {t('app.account.polar.eyebrow')}
                </Meta>
                <span
                    style={{
                        display: 'block',
                        fontSize: 17.5,
                        fontWeight: 400,
                        letterSpacing: '-0.015em',
                        color: c.textPri,
                        marginBottom: 10,
                    }}
                >
                    {t('app.account.polar.title')}
                </span>
                <Muted style={{ display: 'block', fontSize: 13, lineHeight: 1.55 }}>
                    {t('app.account.polar.body')}
                </Muted>
                <Col style={{ marginTop: 22 }} gap={10}>
                    <a
                        href={PORTAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClose}
                        style={{
                            height: 46,
                            borderRadius: 99,
                            background: c.textPri,
                            color: '#0a0a0a',
                            fontFamily: f.ui,
                            fontSize: 14,
                            fontWeight: 500,
                            textDecoration: 'none',
                            display: 'grid',
                            placeItems: 'center',
                        }}
                    >
                        {t('app.account.polar.go')}
                    </a>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            height: 44,
                            borderRadius: 99,
                            background: 'transparent',
                            border: hair,
                            color: c.muted,
                            fontFamily: f.ui,
                            fontSize: 13.5,
                            fontWeight: 400,
                            cursor: 'pointer',
                        }}
                    >
                        {t('app.account.polar.stay')}
                    </button>
                </Col>
            </div>
        </div>
    );
};

const AccountPage: React.FC = () => {
    const { user, userProfile, signOut } = useAuth();
    const { lang, setLang } = useLang();
    const t = useT();
    const navigate = useNavigate();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const desktop = useIsDesktop(DESKTOP);

    const email = userProfile?.email || user?.email || '';
    const letter = (email[0] || '·').toUpperCase();
    const z = scoreColor(profile.index);

    // "Day N" beats a join date: it turns the account screen into a place with
    // history rather than a form. Same trick as the task streak, for free.
    const joined = userProfile?.created_at ? new Date(userProfile.created_at) : null;
    const dayN = joined ? Math.max(1, Math.floor((Date.now() - joined.getTime()) / 86400000) + 1) : null;

    const subFrom = userProfile?.subscription_start_date ? new Date(userProfile.subscription_start_date) : null;
    const fmt = (d: Date) => formatDate(lang, d, { day: 'numeric', month: 'long' });

    const opened = profile.measuredTracks.length;
    const mine = profile.patterns.filter((p) => p.pct != null).length;

    const onSignOut = React.useCallback(async () => {
        await signOut();
        navigate('/');
    }, [signOut, navigate]);

    const [portalOpen, setPortalOpen] = React.useState(false);
    const closePortal = React.useCallback(() => setPortalOpen(false), []);

    const result = (
        <Group title={t('app.account.result')} style={desktop ? { padding: 0 } : { borderTop: hair, paddingTop: 18 }}>
            <Row style={{ border: hair, borderRadius: 16, overflow: 'hidden', alignItems: 'stretch' }}>
                {[
                    [t('app.account.stat.dirs'), String(opened), '/ 8'],
                    [t('app.account.stat.pats'), String(mine), `/ ${content?.patterns.length ?? 53}`],
                    [t('app.account.stat.index'), profile.index == null ? '–' : String(profile.index), null],
                ].map(([label, v, suf], i) => (
                    <Col
                        key={label}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            padding: '14px 12px',
                            borderLeft: i ? hair : 'none',
                            alignItems: 'center',
                        }}
                        gap={6}
                    >
                        <Row style={{ alignItems: 'baseline', gap: 2 }}>
                            <span
                                style={{
                                    fontFamily: f.mono,
                                    fontSize: 22,
                                    fontWeight: 300,
                                    letterSpacing: '-0.035em',
                                    color: i === 2 ? z.hex : c.textPri,
                                }}
                            >
                                {v}
                            </span>
                            {suf && <Meta style={{ fontSize: 10, color: c.subtle }}>{suf}</Meta>}
                        </Row>
                        <Meta style={{ fontSize: 8.5, letterSpacing: '0.11em', textAlign: 'center' }}>{label}</Meta>
                    </Col>
                ))}
            </Row>
            <Link to={R.dirs} style={{ textDecoration: 'none' }}>
                <Row
                    style={{
                        gap: 12,
                        marginTop: 16,
                        background: c.card,
                        border: hair,
                        borderRadius: 14,
                        padding: '13px 15px',
                    }}
                >
                    <Icon k="tDirs" size={16} color={c.muted} />
                    <Col style={{ flex: 1, minWidth: 0 }} gap={3}>
                        <span style={{ fontSize: 14, color: c.textPri, fontWeight: 400 }}>
                            {t(opened >= 8 ? 'app.account.retakeDir' : 'app.account.oneMoreDir')}
                        </span>
                        <Muted style={{ fontSize: 11.5 }}>{t('app.account.dirHint')}</Muted>
                    </Col>
                    <Icon k="arrow" size={15} color={c.muted} />
                </Row>
            </Link>
        </Group>
    );

    const subscription = (
        <Group
            title={t('app.account.subscription')}
            style={desktop ? { padding: 0 } : { marginTop: 22, borderTop: hair, paddingTop: 18 }}
        >
            <Row style={{ gap: 12, paddingBottom: 14, borderBottom: hair2 }}>
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        flex: '0 0 auto',
                        background: userProfile?.has_paid ? '#4ade80' : c.subtle,
                        boxShadow: userProfile?.has_paid ? '0 0 8px #4ade80' : 'none',
                    }}
                />
                <Col style={{ flex: 1, minWidth: 0 }} gap={3}>
                    <span style={{ fontSize: 14.5, color: c.textPri, fontWeight: 400 }}>
                        {t(userProfile?.has_paid ? 'app.account.sub.active' : 'app.account.sub.none')}
                    </span>
                    <Muted style={{ fontSize: 11.5 }}>
                        {subFrom ? t('app.account.sub.since', { date: fmt(subFrom) }) : t('app.account.sub.noDate')}
                    </Muted>
                </Col>
            </Row>
            {/* Cancelling is not buried: it is one line, plainly labelled.
                Polar's customer portal signs the user in by email code and shows
                their subscriptions — same link the refund page already uses. */}
            <Line
                label={t('app.account.manageSub')}
                hint={t('app.account.manageSub.hint')}
                onClick={() => setPortalOpen(true)}
                chev
                last
            />
        </Group>
    );

    const settings = (
        <Group
            title={t('app.account.settings')}
            style={desktop ? { padding: 0 } : { marginTop: 22, borderTop: hair, paddingTop: 18 }}
        >
            <Line
                label={t('app.account.language')}
                last
                value={
                    <Row style={{ gap: 3, background: c.card, border: hair, borderRadius: 99, padding: 3 }}>
                        {(['uk', 'en'] as const).map((code) => {
                            const on = lang === code;

                            return (
                                <button
                                    key={code}
                                    type="button"
                                    onClick={() => setLang(code)}
                                    style={{
                                        padding: '4px 11px',
                                        borderRadius: 99,
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: on ? c.textPri : 'transparent',
                                        fontFamily: f.mono,
                                        fontSize: 10.5,
                                        letterSpacing: '0.08em',
                                        color: on ? '#0a0a0a' : c.muted,
                                    }}
                                >
                                    {code === 'uk' ? 'UA' : 'EN'}
                                </button>
                            );
                        })}
                    </Row>
                }
            />
        </Group>
    );

    const about = (
        <Group
            title={t('app.account.about')}
            style={desktop ? { padding: 0 } : { marginTop: 22, borderTop: hair, paddingTop: 18 }}
        >
            <Line
                label={t('app.account.howScored')}
                hint={t('app.account.howScored.hint')}
                to={R.dirs}
                chev
            />
            <Line label={t('footer.terms')} to="/terms" chev />
            <Line label={t('footer.privacy')} to="/privacy" chev />
            <Line label={t('footer.refund')} to="/refund" chev />
            <Line label={t('app.account.contact')} to="/contact" chev />
            <Line
                label={t('app.account.legacy')}
                hint={t('app.account.legacy.hint')}
                to={R.legacy}
                chev
                last
            />
        </Group>
    );

    const exit = (
        <div style={{ padding: desktop ? '18px 0 0' : '22px 20px 30px' }}>
            <button
                type="button"
                onClick={onSignOut}
                style={{
                    width: '100%',
                    height: 46,
                    borderRadius: 99,
                    background: 'transparent',
                    border: '1px solid rgba(248,113,113,0.24)',
                    color: '#f87171',
                    fontFamily: f.ui,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                }}
            >
                {t('app.account.signOut')}
            </button>
            <Meta style={{ fontSize: 9, color: c.subtle, display: 'block', textAlign: 'center', marginTop: 14 }}>
                {t('app.account.footer', { n: opened })}
            </Meta>
        </div>
    );

    const identity = (
        <Row style={{ gap: 15, padding: desktop ? 0 : '24px 20px 22px' }}>
            <div style={{ position: 'relative', width: 58, height: 58, flex: '0 0 auto' }}>
                <Ring score={profile.index} size={58} sw={2.6} neutral={profile.index == null} />
                <div
                    style={{
                        position: 'absolute',
                        inset: 6,
                        borderRadius: '50%',
                        background: c.pill,
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 19,
                        fontWeight: 400,
                        color: c.textPri,
                    }}
                >
                    {letter}
                </div>
            </div>
            <Col style={{ flex: 1, minWidth: 0 }} gap={7}>
                <span
                    style={{
                        fontSize: 16,
                        fontWeight: 400,
                        letterSpacing: '-0.015em',
                        color: c.textPri,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {email || t('app.account.title')}
                </span>
                <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em' }}>
                    {dayN ? t('app.shell.day', { n: dayN }) : t('app.account.newAccount')}
                </Meta>
            </Col>
        </Row>
    );

    const shellProps = {
        active: 'state' as const,
        title: t('app.account.title'),
        subtitle: dayN ? t('app.shell.day', { n: dayN }) : undefined,
        index: profile.index,
        email,
        mobileHeader: !desktop ? (
            <Row style={{ padding: '14px 16px 12px', gap: 10, borderBottom: hair }}>
                <button
                    type="button"
                    aria-label={t('app.action.back')}
                    onClick={() => navigate(R.state)}
                    style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        border: hair,
                        background: c.card,
                        display: 'grid',
                        placeItems: 'center',
                        flex: '0 0 auto',
                        cursor: 'pointer',
                    }}
                >
                    <Icon k="back" size={15} color={c.textPri} />
                </button>
                <Col style={{ flex: 1, alignItems: 'center' }}>
                    <Meta style={{ color: c.textPri, letterSpacing: '0.18em' }}>{t('app.account.title')}</Meta>
                </Col>
                <div style={{ width: 34, flex: '0 0 auto' }} />
            </Row>
        ) : undefined,
    };

    if (desktop) {
        return (
            <AppShell {...shellProps}>
                <DeskRow>
                    <Fluid>
                        <Panel pad="22px 24px 24px">{identity}</Panel>
                        <Panel pad="18px 22px 20px">{result}</Panel>
                        <Panel pad="18px 22px 8px">{about}</Panel>
                    </Fluid>
                    <Rail width={340}>
                        <Panel pad="18px 22px 8px">{subscription}</Panel>
                        <Panel pad="18px 22px 8px">{settings}</Panel>
                        <Panel pad="18px 22px 20px">{exit}</Panel>
                    </Rail>
                </DeskRow>
                {portalOpen && <PortalModal onClose={closePortal} />}
            </AppShell>
        );
    }

    return (
        <AppShell {...shellProps}>
            {identity}
            {result}
            {subscription}
            {settings}
            {about}
            {exit}
            {portalOpen && <PortalModal onClose={closePortal} />}
        </AppShell>
    );
};

export default AccountPage;
